import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

/**
 * 参数示例:
 * ```typescript
 * {
 *   "input": {
 *     "image_url": "https://example.com/human_image.jpg"
 *   },
 *   "parameters": {}
 * }
 * ```
 */
export interface AlibabaAnimateAnyoneDetectParams {
  /**
   * 输入参数，包含待检测的图片URL
   */
  input: {
    /**
     * 图片URL，必须是有效的HTTP或HTTPS URL，系统会自动转换为Base64格式
     */
    image_url: string;
  };
  /**
   * 额外参数（可选）
   */
  parameters?: Record<string, any>;
}

export interface AlibabaAnimateAnyoneDetectResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * alibaba_animate_anyone_detect
 *
 * Alibaba AnimateAnyone Detect 人体图像检测接口，用于在视频生成前验证人体图片是否符合AnimateAnyone模型要求。
 * 
 * Model: animate-anyone-detect-gen2
 * Features: 
 * - 检测图片中的人体是否符合视频生成要求
 * - 返回人体姿态风格信息
 * - 提供未通过检测的详细原因
 * 
 * API Documentation: https://help.aliyun.com/zh/model-studio/animate-anyone-detect-api
 *
 * @example
 * ```typescript
 * import { initSeacloud, alibabaAnimateAnyoneDetect } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await alibabaAnimateAnyoneDetect(
 *   {
 *     "input": {
 *       "image_url": "https://example.com/human_image.jpg"
 *     },
 *     "parameters": {}
 *   }
 * );
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 AlibabaAnimateAnyoneDetectParams 接口定义
 * @returns 生成的资源列表
 */
export async function alibabaAnimateAnyoneDetect(
  params: AlibabaAnimateAnyoneDetectParams,
): Promise<AlibabaAnimateAnyoneDetectResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'alibaba_animate_anyone_detect',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: AlibabaAnimateAnyoneDetectResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
